'use client';

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from 'react';
import { flushSync } from 'react-dom';

import { Button, HeroIcon, Typography } from '@/components/atoms';
import { LinkButton } from '@/components/molecules';
import {
  ContentCard,
  ContentCardVariant,
} from '@/components/organisms/ContentCard';
import { CarouselShell } from '@/components/molecules/Carousel/CarouselShell';
import { cn } from '@/lib/utils/tailwind';
import { useTranslations } from 'next-intl';

import type { MediaRailProps } from './types';

const SLIDE_DURATION = 450;
const SWIPE_THRESHOLD = 50;

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export const MediaRail = ({
  items,
  title,
  className,
  showAllURL,
  contentType,
}: MediaRailProps) => {
  const t = useTranslations();
  const params = new URLSearchParams(showAllURL?.split('?')[1] ?? showAllURL);
  const widgetId = params.get('widgetId');
  const [state, setState] = useState({
    focusIndex: 0,
    targetOffset: 0,
  });
  const { focusIndex, targetOffset } = state;
  const [isAnimating, setIsAnimating] = useState(false);
  const [widths, setWidths] = useState({
    poster: 0,
    expanded: 0,
    gap: 0,
    isMobile: false,
  });
  const sectionRef = useRef<HTMLElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const baseOffsetRef = useRef(0);
  const dragStartX = useRef<number | null>(null);

  const calculateWidths = useCallback(() => {
    if (typeof window === 'undefined' || !trackRef.current) return;
    const style = getComputedStyle(trackRef.current);
    const padding = parseFloat(style.paddingLeft);
    const gap = parseFloat(style.columnGap);
    const containerWidth = sectionRef.current?.clientWidth || window.innerWidth;
    const isMobile = containerWidth < 768;
    const available = containerWidth - padding - (isMobile ? 1 : 3) * gap;
    setWidths({
      poster: isMobile ? available * 0.25 : available / 6,
      expanded: isMobile ? available * 0.75 : available / 2,
      gap,
      isMobile,
    });
  }, []);

  useIsomorphicLayoutEffect(() => {
    calculateWidths();
    window.addEventListener('resize', calculateWidths);
    return () => window.removeEventListener('resize', calculateWidths);
  }, [calculateWidths]);

  useIsomorphicLayoutEffect(() => {
    if (widths.poster > 0) {
      baseOffsetRef.current = widths.poster + widths.gap;
      if (trackRef.current && !isAnimating) {
        trackRef.current.style.transition = 'none';
        trackRef.current.style.transform = `translateX(-${baseOffsetRef.current}px)`;
      }
    }
  }, [widths.poster, widths.gap, widths.isMobile, isAnimating]);

  const navigate = useCallback(
    (steps: number) => {
      if (isAnimating || steps === 0 || items.length === 0) return;

      setIsAnimating(true);
      const newIndex = focusIndex + steps;

      const slideAmount = steps * (widths.poster + widths.gap);

      if (trackRef.current) {
        trackRef.current.style.transition = `transform ${SLIDE_DURATION}ms cubic-bezier(0.25, 0.1, 0.25, 1)`;
        trackRef.current.style.transform = `translateX(-${baseOffsetRef.current + slideAmount}px)`;
      }

      setState(prev => ({ ...prev, targetOffset: steps }));

      setTimeout(() => {
        flushSync(() => {
          setState({
            focusIndex: newIndex,
            targetOffset: 0,
          });
          if (trackRef.current) {
            trackRef.current.style.transition = 'none';
            trackRef.current.style.transform = `translateX(-${baseOffsetRef.current}px)`;
          }
          setIsAnimating(false);
        });
      }, SLIDE_DURATION);
    },
    [isAnimating, items.length, widths.poster, widths.gap, focusIndex],
  );

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        navigate(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        navigate(-1);
      }
    };
    section.addEventListener('keydown', onKeyDown);
    return () => section.removeEventListener('keydown', onKeyDown);
  }, [navigate]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      dragStartX.current = e.clientX;
    },
    [],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (dragStartX.current === null) return;
      const delta = e.clientX - dragStartX.current;
      dragStartX.current = null;
      if (Math.abs(delta) >= SWIPE_THRESHOLD) {
        navigate(delta < 0 ? 1 : -1);
      }
    },
    [navigate],
  );

  const handlePointerCancel = useCallback(() => {
    dragStartX.current = null;
  }, []);

  if (items.length < 4) return null;

  const renderItems = [];
  const startPos = -1;
  const endPos = widths.isMobile ? 2 : 4;
  for (let i = startPos; i <= endPos; i++) {
    const itemIndex =
      (((focusIndex + i) % items.length) + items.length) % items.length;
    renderItems.push({
      ...items[itemIndex],
      isExpanded: i === targetOffset,
      position: i,
    });
  }

  const currentItem =
    items[
      (((focusIndex + targetOffset) % items.length) + items.length) %
        items.length
    ];

  return (
    <section
      ref={sectionRef}
      data-widget-id={widgetId}
      id={`card-carousel-${widgetId}`}
      className={cn(
        'carousel-section w-full overflow-hidden bg-(--color-surface-default) pb-6 text-(--color-text-primary)',
        className,
      )}
      aria-label={title ?? 'Media carousel'}
      style={
        {
          '--poster-width': `${widths.poster}px`,
          '--poster-height': `${widths.poster * (4 / 3)}px`,
          '--expanded-width': `${widths.expanded}px`,
          '--expanded-height': `${widths.expanded * 0.5625}px`,
        } as React.CSSProperties
      }
    >
      <CarouselShell
        title={title}
        titleClassName="mb-5 mx-4 italic text-(--color-text-primary)"
        headerAction={
          showAllURL ? (
            <LinkButton
              variant="tertiary"
              href={showAllURL}
              size="sm"
              className="mx-4 whitespace-nowrap"
              onClick={() => {
                if (typeof window !== 'undefined' && widgetId) {
                  sessionStorage.setItem('lastVisitedWidget', widgetId);
                }
              }}
            >
              {t('FOCAL_NAV_SEE_ALL')}
            </LinkButton>
          ) : undefined
        }
        footer={
          <div className="content-description mx-(--layout-inline-padding) border-t border-(--color-border-neutral-softer-3) pt-6 min-h-35">
            <div
              className={cn(
                'description-inner transition-opacity duration-200',
                isAnimating ? 'opacity-0' : 'opacity-100',
              )}
            >
              <Typography.Heading
                variant="h5"
                className="mb-5 italic uppercase text-(--color-text-primary)"
              >
                {currentItem.title}
              </Typography.Heading>
              <Typography.Text
                variant="body"
                size="lg"
                className="max-w-180 italic leading-relaxed line-clamp-2 text-(--color-text-secondary)"
              >
                {currentItem.description}
              </Typography.Text>
            </div>
          </div>
        }
      >
        <div
          className="carousel-viewport relative min-h-[calc(var(--expanded-height)+100px)] py-5 select-none"
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          onDragStart={e => e.preventDefault()}
          style={{ touchAction: 'pan-y' }}
        >
          <Button
            variant="secondary"
            onClick={() => navigate(-1)}
            onMouseDown={e => e.preventDefault()}
            aria-label={t('PREVIOUS_SLIDE')}
            className="absolute left-4 top-[calc(var(--expanded-height)/2)] z-20 -translate-y-1/2 shadow-md"
          >
            <HeroIcon icon={ChevronLeftIcon} size="small" />
          </Button>

          <div
            ref={trackRef}
            className="carousel-track flex items-start gap-3 pl-(--layout-inline-padding)"
            style={{
              willChange: 'transform',
              height: 'calc(var(--expanded-height) + 60px)',
            }}
          >
            {renderItems.map(item => (
              <ContentCard
                key={`${item.id}-${focusIndex + item.position}`}
                variant={ContentCardVariant.MEDIA_RAIL}
                title={item.title}
                subtitle={item.subtitle}
                poster={item.poster}
                videoGif={item.videoGif}
                videoClip={item.videoClip}
                isExpanded={item.isExpanded}
                contentType={contentType}
                onClick={() => {
                  if (item.position !== targetOffset) {
                    navigate(item.position - targetOffset);
                  }
                }}
                watchNowHref={item.link}
              />
            ))}
          </div>

          <Button
            variant="secondary"
            onClick={() => navigate(1)}
            onMouseDown={e => e.preventDefault()}
            aria-label={t('NEXT_SLIDE')}
            className="absolute right-4 top-[calc(var(--expanded-height)/2)] z-20 -translate-y-1/2 shadow-md"
          >
            <HeroIcon icon={ChevronRightIcon} size="small" />
          </Button>
        </div>
      </CarouselShell>
    </section>
  );
};
